import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  markOfferPopupSeen,
  nextPopupOffer,
  offerDeadline,
  offerDetailPath,
  offerReward,
  offerTarget,
} from "./offerHelpers";

/**
 * Shows an offer on the way in, when the office marked it for that, once per browser. It is marked
 * as seen as soon as it appears, so a partner who signs out without closing it is not shown it
 * again. The description is cut short here; the full offer is one click away on its own page.
 */
const OfferPopup = ({ offers }) => {
  const [offer, setOffer] = useState(null);

  // Keep the offer on screen while it is open: a background refetch hands in a new array, and by
  // then this offer is already marked seen, so re-picking would close it under the partner.
  useEffect(() => {
    setOffer((current) => current || nextPopupOffer(offers));
  }, [offers]);

  useEffect(() => {
    if (offer) markOfferPopupSeen(offer.id);
  }, [offer]);

  if (!offer) return null;

  const close = () => setOffer(nextPopupOffer(offers));

  const reward = offerReward(offer);
  const target = offerTarget(offer);
  const closes = offerDeadline(offer);
  const image = (offer.imageUrls || [])[0];

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={offer.title}
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
    >
      <div className="flex w-full max-w-md max-h-full flex-col overflow-hidden rounded-2xl bg-white shadow-xl">
        {image ? (
          <img
            src={image}
            alt=""
            className="w-full shrink-0 object-cover"
            style={{ maxHeight: 180 }}
          />
        ) : null}
        <div className="min-h-0 flex-1 overflow-y-auto p-5">
          <p className="text-xs font-semibold uppercase tracking-wide text-amber-600 m-0">
            Partner offer
          </p>
          <h2 className="text-lg font-semibold text-slate-900 m-0 mt-1 break-words">
            {offer.title}
          </h2>
          {target ? (
            <p className="text-sm text-slate-700 m-0 mt-1">
              {reward ? `${reward} for ${target}` : target}
            </p>
          ) : null}
          {offer.description ? (
            <p className="text-sm text-slate-600 m-0 mt-3 whitespace-pre-line break-words line-clamp-6">
              {offer.description}
            </p>
          ) : null}
          {closes ? <p className="text-xs text-slate-500 m-0 mt-3">Open until {closes}.</p> : null}
        </div>
        <div className="shrink-0 flex gap-2 border-t border-slate-100 p-4">
          <Link
            to={offerDetailPath(offer)}
            onClick={close}
            className="flex-1 rounded-lg border border-slate-200 px-4 py-2 text-center text-sm font-semibold text-slate-700 no-underline hover:border-teal-300"
          >
            View full offer
          </Link>
          <button
            type="button"
            onClick={close}
            className="flex-1 rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white"
          >
            {offer.ctaLabel || "Got it"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OfferPopup;
